<script lang="ts">
  import { onMount } from 'svelte';
  import { DEFAULT_ORG_SETTINGS, orgSettingsAccess, putOrgSettings, type OrgSettings } from '../lib/api/server';
  import { HttpError } from '../lib/api/cache';
  import { nav } from '../lib/nav.svelte';
  import { policyNotFoundText } from '../lib/logic/policy';

  interface Props {
    org: string;
  }
  let { org }: Props = $props();

  let settings: OrgSettings = $state({ ...DEFAULT_ORG_SETTINGS });
  let origins = $state('');
  /**
   * `pending`: an earlier policy is not resolved yet, so none is in force; an
   * admin's save replaces it. `personal`: a personal account, whose fixed
   * policy nobody changes.
   */
  let status: 'loading' | 'ready' | 'pending' | 'personal' | 'error' = $state('loading');
  let message = $state('');
  let saving = $state(false);
  let canEdit = $state(false);

  const repo = nav.query.get('repo') ?? undefined;

  onMount(async () => {
    try {
      // `?repo=`: read through a repository of the account (outside collaborators, other users' accounts).
      const got = await orgSettingsAccess(org, repo);
      canEdit = got.canEdit;
      settings = got.status === 'pending' ? { ...DEFAULT_ORG_SETTINGS } : got.settings;
      origins = settings.brief_origins.join('\n');
      status = got.status;
    } catch (e) {
      message = e instanceof HttpError && e.status === 404 ? policyNotFoundText(org, repo) : String(e);
      status = 'error';
    }
  });

  async function save() {
    saving = true;
    message = '';
    const next: OrgSettings = {
      ...settings,
      brief_origins: origins
        .split(/[\s,]+/)
        .map((o) => o.trim())
        .filter(Boolean),
    };
    try {
      await putOrgSettings(org, next);
      settings = next;
      status = 'ready';
      message = 'Saved.';
    } catch (e) {
      message =
        e instanceof HttpError && e.status === 403
          ? `Only ${org} organization admins can change these settings.`
          : `Couldn’t save: ${e instanceof Error ? e.message : e}`;
    } finally {
      saving = false;
    }
  }
</script>

<header class="top"><div class="eyebrow">{status === 'personal' ? 'Personal account' : 'Organization'} · {org}</div></header>
<h2 class="section-title">{org} review policy</h2>
{#if status === 'personal'}
  <p class="banner read-only" role="note">
    {org} is a personal account. Personal accounts have no organization policy: this fixed policy applies to every
    repository {org} owns, and nobody can change it.
  </p>
  <dl class="card settings read-only fixed-policy">
    <div class="setting block">
      <dt><b>Merging from Changeling</b></dt>
      <dd>{settings.allow_merge ? 'Allowed' : 'Not allowed'}<small>The merge button is offered when GitHub would allow the merge.</small></dd>
    </div>
    <div class="setting block">
      <dt><b>Brief origins</b></dt>
      <dd>
        {#if settings.brief_origins.length}{settings.brief_origins.join(', ')}{:else}Any https: origin{/if}<small
          >Pages linked as brief tabs in a pull request's description show inside Changeling.</small
        >
      </dd>
    </div>
  </dl>
{/if}
{#if status === 'pending' && canEdit}
  <p class="banner read-only" role="note">
    This organization’s earlier policy hasn’t been resolved yet. Saving here replaces it.
  </p>
{:else if status === 'pending'}
  <p class="banner read-only" role="note">
    {org}’s earlier policy hasn’t been resolved yet, so no policy is in force and Changeling does not merge pull requests
    of {org}. An admin of {org} can replace it by saving its settings here.
  </p>
{/if}
{#if status === 'ready' && !canEdit}
  <p class="banner read-only" role="note">
    You can see {org}’s policy but not change it: only organization admins can. Ask an admin of {org} if something here
    should change.
  </p>
{/if}
{#if status === 'loading'}
  <p class="loading">Loading…</p>
{:else if status === 'error'}
  <div class="card"><p class="muted">{message}</p></div>
{:else if status === 'ready' || canEdit}
  <form
    class="card settings"
    class:read-only={!canEdit}
    onsubmit={(e) => {
      e.preventDefault();
      void save();
    }}
  >
    <label class="setting">
      <input type="checkbox" bind:checked={settings.allow_merge} disabled={!canEdit} />
      <span><b>Allow merging from Changeling</b><small>Offer the merge button when GitHub would allow the merge.</small></span>
    </label>
    <label class="setting block">
      <span><b>Allowed brief origins</b><small>Origins whose pages may appear as tabs on a pull request, one per line (for example <code>https://briefs.example.com</code>). Leave empty to allow any https: origin. Pages are embedded only for authors who are owners, members or collaborators of the repository; anyone else's appear as links.</small></span>
      <textarea rows="4" bind:value={origins} aria-label="Allowed brief origins" readonly={!canEdit}></textarea>
    </label>
    <div class="sheet-actions">
      <span class="muted small" role="status">{message}</span>
      {#if canEdit}<button class="btn primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>{/if}
    </div>
  </form>
{/if}
